import { useEffect, useMemo, useRef, useState } from "react";
import type { JSX } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { subDays, startOfDay, endOfDay } from "date-fns";
import { Filter } from "lucide-react";
import { AnalyticsFiltersDrawer } from "@/features/analytics/components/AnalyticsFiltersDrawer";
import { useAnalyticsApp } from "@/features/analytics/context";
import type {
  AnalyticsDateRange,
  AnalyticsFilters,
} from "@/features/analytics/types";
import {
  cloneAnalyticsFilters,
  countActiveAnalyticsFilters,
  createEmptyAnalyticsFilters,
} from "@/features/analytics/utils/analytics";
import { DeviceTable } from "@/features/devices/components/DeviceTable";
import { useDeviceAggregates } from "@/features/devices/hooks/useDeviceAggregates";
import { useDeviceCount } from "@/features/devices/hooks/useDeviceCount";
import { useDeviceList } from "@/features/devices/hooks/useDeviceList";
import {
  buildDevicesUrlSearch,
  parseDevicesUrlState,
} from "@/features/devices/utils";
import { usePageTitle } from "@/components/common/AppShell";
import DateRangePicker from "@/components/common/DateRangePicker";

const PAGE_SIZE = 25;

type PickerRange = { from: Date; to: Date };

const defaultRange = (): PickerRange => {
  const to = endOfDay(new Date());
  const from = startOfDay(subDays(new Date(), 6));
  return { from, to };
};

const PAGE_BTN =
  "inline-flex items-center gap-2 rounded-(--radius-md) border border-(--border-strong) bg-(--surface-1) px-[14px] py-[9px] text-[13px] font-semibold text-(--text-primary) transition-colors hover:border-(--brand-primary) disabled:cursor-not-allowed disabled:opacity-60";

const DevicesPage = (): JSX.Element => {
  usePageTitle("Devices");
  const { campaigns, availableChannels, availableCategories, availableTags } =
    useAnalyticsApp();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.replace(/\/devices.*$/, "");

  const initial = useMemo(() => {
    const parsed = parseDevicesUrlState(new URLSearchParams(location.search));
    return {
      range: parsed.range ?? defaultRange(),
      filters: parsed.filters,
      page: parsed.page,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [pickerRange, setPickerRange] = useState<PickerRange>(initial.range);
  const [appliedFilters, setAppliedFilters] = useState<AnalyticsFilters>(
    initial.filters,
  );
  const [draftFilters, setDraftFilters] = useState<AnalyticsFilters>(
    initial.filters,
  );
  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);
  const [page, setPage] = useState(initial.page);

  const isFirstSyncRef = useRef(true);
  useEffect(() => {
    const next = buildDevicesUrlSearch(appliedFilters, pickerRange, page);
    const current = location.search.replace(/^\?/, "");
    if (next === current) {
      isFirstSyncRef.current = false;
      return;
    }
    const replace = isFirstSyncRef.current;
    isFirstSyncRef.current = false;
    navigate(`${location.pathname}?${next}`, { replace });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, pickerRange, page]);

  const handleOpenDevice = (deviceId: string): void => {
    navigate(`${basePath}/device/${encodeURIComponent(deviceId)}`);
  };

  const selectedRange: AnalyticsDateRange = useMemo(
    () => ({ startDate: pickerRange.from, endDate: pickerRange.to }),
    [pickerRange.from, pickerRange.to],
  );

  const { rows, isLoading, errorMessage } = useDeviceList({
    selectedRange,
    appliedFilters,
    page,
    pageSize: PAGE_SIZE,
  });

  const deviceIds = useMemo(() => rows.map((row) => row.deviceId), [rows]);
  const { aggregates, isLoading: aggregatesLoading } = useDeviceAggregates({
    deviceIds,
    selectedRange,
    appliedFilters,
  });

  const { totalDevices } = useDeviceCount({ selectedRange, appliedFilters });

  const totalPages =
    totalDevices != null ? Math.max(1, Math.ceil(totalDevices / PAGE_SIZE)) : 1;
  const activeFiltersCount = countActiveAnalyticsFilters(appliedFilters);

  const handleOpenFilters = (): void => {
    setDraftFilters(cloneAnalyticsFilters(appliedFilters));
    setIsFiltersDrawerOpen(true);
  };

  const handleApplyFilters = (): void => {
    setAppliedFilters(cloneAnalyticsFilters(draftFilters));
    setIsFiltersDrawerOpen(false);
    setPage(1);
  };

  const handleResetFilters = (): void => {
    setDraftFilters(createEmptyAnalyticsFilters());
  };

  const handleRangeChange = (next: PickerRange): void => {
    setPickerRange(next);
    setPage(1);
  };

  return (
    <section className="flex min-w-0 flex-col">
      <div className="mb-[26px] flex flex-wrap items-start justify-between gap-4">
        <p className="text-[13.5px] text-(--text-muted)">
          Browse devices that received your campaigns and inspect their
          activity.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <DateRangePicker value={pickerRange} onChange={handleRangeChange} />
          <button
            type="button"
            onClick={handleOpenFilters}
            className={PAGE_BTN}
          >
            <Filter
              size={15}
              strokeWidth={1.8}
              className="text-(--brand-primary)"
            />
            Filters
            {activeFiltersCount > 0 ? (
              <span className="inline-flex min-w-[22px] items-center justify-center rounded-(--radius-pill) bg-(--pastel-violet) px-2 py-0.5 text-[11px] font-bold text-(--brand-primary)">
                {activeFiltersCount}
              </span>
            ) : null}
          </button>
        </div>
      </div>

      {errorMessage ? (
        <div className="mb-4 rounded-(--radius-md) border border-(--status-danger)/30 bg-(--pastel-rose) px-4 py-3 text-[13px] text-(--status-danger)">
          {errorMessage}
        </div>
      ) : null}

      <DeviceTable
        rows={rows}
        onOpenDevice={handleOpenDevice}
        isLoading={isLoading}
        aggregates={aggregates}
        aggregatesLoading={aggregatesLoading}
      />

      <div className="mt-[22px] flex items-center justify-between">
        <span className="text-[13px] text-(--text-muted)">
          {isLoading
            ? "Loading…"
            : totalDevices != null
              ? `Page ${page} of ${totalPages} • ${totalDevices.toLocaleString(
                  "en-US",
                )} devices`
              : `Page ${page} • counting…`}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={PAGE_BTN}
            disabled={page <= 1}
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
          >
            Previous
          </button>
          <button
            type="button"
            className={PAGE_BTN}
            disabled={totalDevices != null && page >= totalPages}
            onClick={() =>
              setPage((prev) =>
                totalDevices != null
                  ? Math.min(totalPages, prev + 1)
                  : prev + 1,
              )
            }
          >
            Next
          </button>
        </div>
      </div>

      <AnalyticsFiltersDrawer
        isOpen={isFiltersDrawerOpen}
        value={draftFilters}
        campaigns={campaigns}
        channels={availableChannels}
        categories={availableCategories}
        tags={availableTags}
        onChange={setDraftFilters}
        onClose={() => setIsFiltersDrawerOpen(false)}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />
    </section>
  );
};

export default DevicesPage;
